import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import {
  Addition,
  CasterProcessData,
  Charge,
  EAFProcessData,
  HeatData,
  Heating,
  LMFProcessData,
  Probe,
} from "../models/HeatReportModels";
import { ProcessPhase, Slag } from "@/pages/eaf/data-entry/models/eaf.models";
import { DelayByShift } from "@/pages/delays/delays-manager/models/DelayByShift";

interface Props {
  HeatId: number | null;
}
const moment = getMomentTz();

export const getHeatReport = async ({ HeatId }: Props) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "HeatId", value: HeatId });

  const apiService = new MESApiService();
  const resp = await apiService.callV2("[RBD].[GetHeatReport]", sqlParameters);
  if (resp.ok) {
    resp.data.tables[0].rows = resp.data.tables[0].rows.map(
      (row: HeatData) => ({
        ...row,
        TapDate: row.TapDate ? moment.utc(row.TapDate).toDate() : null,
        OpenDate: row.OpenDate ? moment.utc(row.OpenDate).toDate() : null,
      }),
    );
  }
  return resp;
};

export const getEAFProcessReport = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: ProcessId });

  const apiService = new MESApiService();
  const resp = await apiService.callV2("[EAF].[DEGetProcess]", sqlParameters);
  if (resp.ok) {
    resp.data.tables[0].rows = resp.data.tables[0].rows.map(
      (row: EAFProcessData) => ({
        ...row,
        Start: row.Start ? moment.utc(row.Start).toDate() : null,
        End: row.End ? moment.utc(row.End).toDate() : null,
        StartTap: row.StartTap ? moment.utc(row.StartTap).toDate() : null,
        StartHeat: row.StartHeat ? moment.utc(row.StartHeat).toDate() : null,
      }),
    );
    resp.data.tables[1].rows = resp.data.tables[1].rows.map((row: Probe) => ({
      ...row,
      Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    }));
    resp.data.tables[2].rows = resp.data.tables[2].rows.map((row: Charge) => ({
      ...row,
      Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    }));
    resp.data.tables[3].rows = resp.data.tables[3].rows.map(
      (row: Addition) => ({
        ...row,
        Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
      }),
    );
    resp.data.tables[4].rows = resp.data.tables[4].rows.map(
      (row: ProcessPhase) => ({
        ...row,
        Start: row.Start ? moment.utc(row.Start).toDate() : null,
        End: row.End ? moment.utc(row.End).toDate() : null,
      }),
    );
    resp.data.tables[6].rows = resp.data.tables[6].rows.map((row: Slag) => ({
      ...row,
      Time: row.Time ? moment.utc(row.Time).toDate() : null,
    }));
    resp.data.tables[8].rows = resp.data.tables[8].rows.map((row: Heating) => ({
      ...row,
      Start: row.StartTime ? moment.utc(row.StartTime).toDate() : null,
      End: row.EndTime ? moment.utc(row.EndTime).toDate() : null,
    }));
  }
  return resp;
};

export const getLMFProcessReport = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: ProcessId });

  const apiService = new MESApiService();
  const resp = await apiService.callV2("[LMF].[DEGetProcess]", sqlParameters);
  if (resp.ok) {
    resp.data.tables[0].rows = resp.data.tables[0].rows.map(
      (row: LMFProcessData) => ({
        ...row,
        Start: row.Start ? moment.utc(row.Start).toDate() : null,
        End: row.End ? moment.utc(row.End).toDate() : null,
        Arrive: row.Arrive ? moment.utc(row.Arrive).toDate() : null,
        Ship: row.Ship ? moment.utc(row.Ship).toDate() : null,
      }),
    );
    resp.data.tables[1].rows = resp.data.tables[1].rows.map((row: Probe) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
    resp.data.tables[2].rows = resp.data.tables[2].rows.map((row: Heating) => {
      row.StartTime = moment.utc(row["StartTime"]).toDate();
      row.EndTime = moment.utc(row["EndTime"]).toDate();
      return row;
    });
    resp.data.tables[3].rows = resp.data.tables[3].rows.map((row: Addition) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
  }
  return resp;
};

export const getCASProcessReport = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: ProcessId });

  const apiService = new MESApiService();
  const resp = await apiService.callV2("[CAS].[DEGetProcess]", sqlParameters);
  if (resp.ok) {
    resp.data.tables[0].rows = resp.data.tables[0].rows.map(
      (row: CasterProcessData) => ({
        ...row,
        LadleOpen: row.LadleOpen ? moment.utc(row.LadleOpen).toDate() : null,
        LadleClose: row.LadleClose ? moment.utc(row.LadleClose).toDate() : null,
      }),
    );
  }
  return resp;
};

export const getTorchesReport = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: ProcessId });

  const apiService = new MESApiService();
  return await apiService.callV2("[CAS].[DEGetTorches]", sqlParameters);
};

export const searchHeatsWithProcessId = async (searchTerm: string) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "SearchTerm", value: searchTerm },
    { name: "MaxResults", value: 100 },
  ];

  return await apiService.callV2("[MS].[SearchHeatsWithProcessId]", parameters);
};

export const getLabReport = async ({ ProcessId }: { ProcessId: number }) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: ProcessId });
  const apiService = new MESApiService();
  return await apiService.callV2("[LAB].[DEGetProcess]", sqlParameters);
};

export const getEAFDelays = async (processId: number) => {
  const sqlParameters = [];
  sqlParameters.push({ name: "ProcessId", value: processId });

  const apiService = new MESApiService();
  const resp = await apiService.callV2(
    "[DEL].[GetDelaysByProcessId]",
    sqlParameters,
  );
  if (resp.ok) {
    resp.data.tables[0].rows = resp.data.tables[0].rows.map(
      (row: DelayByShift) => ({
        ...row,
        Start: row.Start ? moment.utc(row.Start).toDate() : null,
        End: row.End ? moment.utc(row.End).toDate() : null,
      }),
    );
  }
  return resp;
};
